import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { v, num, PLACEHOLDER, FieldRow, KeepTogether, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartyProjection, PartyBlock, COTENANT_SUFFIX, } from '../../../parties/index.js';
export const metadata = {
    sections: [
        { ref: 'hyresvard', title: 'Hyresvärd' },
        { ref: 'hyresgast', title: 'Hyresgäst' },
        { ref: 'hyresavtalet', title: 'Hyresavtalet' },
        { ref: 'tidsbestamd-hyra', title: 'Tidsbestämd hyra' },
        { ref: 'underskrifter', title: 'Underskrifter' },
    ],
    templateKey: 'tillaggsavtal_tidsbestamd_hyra',
    version: 'v1',
    category: 'Bostadshyra',
    title: 'Tilläggsavtal om tidsbestämd hyra',
    shortTitle: 'Tidsbestämd hyra',
    formNumber: 'Formulär R-22',
    preamble: 'Tillägg till bostadshyresavtalet om en på förhand bestämd hyresutveckling (tidsbestämd hyra).',
    description: 'Tillägg till ett bostadshyresavtal (även studentbostad) om tidsbestämd hyra: en på förhand överenskommen hyresutveckling där hyran justeras ett bestämt datum varje år med en angiven höjningsprocent.\n\n' +
        'Vem undertecknar: hyresvärden och hyresgästen. Skickas via Scrive.\n\n' +
        'Kopplad mall — skapas under det bostadshyresavtal som tillägget avser. Parter och inflyttningshyra förförifylls från avtalet. Ange hyresperioden och en rad per justering (datum + höjningsprocent); den beloppsbestämda månadshyran i kronor räknas ut automatiskt.',
    status: 'active',
    signingMode: 'scrive',
    connectedTo: ['bostadshyra', 'studentbostad'],
    signingPlacementAnchors: 'per-party-label',
    // Same lease, same parties + lägenhet (ADR-0055). Fastigheten hårdlänkas
    // via `property: true` (fastighetsbeteckning/kommun/postadress speglas från
    // den länkade fastigheten). Inflyttningshyran ärvs från förälderns
    // månadshyra (`rent_amount` — samma nyckel i både bostadshyra och
    // studentbostad); `parent_contract_number` bär det avtalsnummer tillägget
    // avser och `property_official_apt_no` det officiella lägenhetsnumret (samma
    // nycklar i båda föräldramallarna). Tilläggsavtalets egna `contract_number`
    // ärvs aldrig — det är en valfri egen referens som genereras/fylls i här.
    inheritFromParent: {
        parties: 'by-role-ordinal',
        property: true,
        values: [
            { key: 'move_in_rent', fromParentKey: 'rent_amount' },
            { key: 'parent_contract_number', fromParentKey: 'contract_number' },
            { key: 'apartment_official_number', fromParentKey: 'property_official_apt_no' },
        ],
    },
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            role: 'lessor',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'lessee_primary',
            label: 'Hyresgäst',
            role: 'lessee',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'lessee_co',
            label: 'Medhyresgäst',
            role: 'lessee',
            ordinal: 1,
            signs: true,
            help: 'Valfritt — lägg till om avtalet har två hyresgäster.',
        },
    ],
    // Party projection (ADR-0065).
    partyProjection: [
        {
            slot: 'lessor',
            section: 'hyresvard',
            idHint: 'org',
            fields: [
                { attr: 'legal_name', key: 'landlord_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'landlord_org_number' },
            ],
        },
        {
            slot: 'lessee_primary',
            section: 'hyresgast',
            fields: [
                { attr: 'legal_name', key: 'tenant_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'tenant_id_number' },
            ],
        },
        {
            slot: 'lessee_co',
            section: 'hyresgast',
            labelSuffix: COTENANT_SUFFIX,
            fields: [
                { attr: 'legal_name', key: 'cotenant_name' },
                { attr: 'id_number', key: 'cotenant_id_number' },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'property',
            label: 'Fastighet',
            multi: false,
            required: true,
        },
    ],
};
export const variableSchema = [
    // Avtalsuppgifter — tilläggsavtalets egna (valfria) avtalsnummer.
    // Inte detsamma som hyresavtalets "Hyreskontrakt nr" nedan.
    {
        key: 'contract_number',
        label: 'Avtalsnummer',
        type: 'text',
        section: 'Avtalsuppgifter',
        help: 'Valfritt — egen referens på tilläggsavtalet.',
        // Per-contract identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    // Parter — generated from metadata.partyProjection (ADR-0065).
    ...expandPartyProjection(metadata),
    // Hyresavtalet — fastighet + lägenhet ärvs från föräldraavtalet.
    {
        key: 'parent_contract_number',
        label: 'Hyreskontrakt nr',
        type: 'text',
        section: 'hyresavtalet',
        requiredAtFinal: true,
        help: 'Numret på det bostadshyresavtal som tillägget avser. Förifylls från avtalet.',
        // Per-contract identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    {
        key: 'property_designation',
        label: 'Fastighetsbeteckning',
        type: 'text',
        section: 'hyresavtalet',
        requiredAtFinal: true,
        help: 'Förifylls från hyresavtalets fastighet.',
        autofill: { kind: 'property', field: 'name' },
    },
    {
        key: 'property_municipality',
        label: 'Kommun',
        type: 'text',
        section: 'hyresavtalet',
        help: 'Förifylls från hyresavtalets fastighet.',
        autofill: { kind: 'property', field: 'kommun' },
    },
    {
        key: 'property_address',
        label: 'Postadress',
        type: 'textarea',
        section: 'hyresavtalet',
        help: 'Förifylls från hyresavtalets fastighet.',
        autofill: { kind: 'property', field: 'address' },
    },
    {
        key: 'apartment_official_number',
        label: 'Officiellt lägenhetsnummer',
        type: 'text',
        section: 'hyresavtalet',
        help: 'Förifylls från hyresavtalet.',
        // Per-unit identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    // Tidsbestämd hyra
    {
        key: 'move_in_rent',
        label: 'Hyra vid inflyttningen (kr/månad)',
        type: 'currency',
        min: 0,
        section: 'tidsbestamd-hyra',
        requiredAtFinal: true,
        help: 'Inflyttningshyran. Hämtas från hyresavtalet — utgångspunkt för beräkningen.',
        // Inherited from parent; recomputed there, so never copied (ADR-0050).
        scopes: { copy: 'manual' },
    },
    {
        key: 'period_start',
        label: 'Period från och med',
        type: 'date',
        section: 'tidsbestamd-hyra',
        requiredAtFinal: true,
        scopes: { copy: 'manual' },
    },
    {
        key: 'period_end',
        label: 'Period till och med',
        type: 'date',
        section: 'tidsbestamd-hyra',
        requiredAtFinal: true,
        scopes: { copy: 'manual' },
    },
    {
        key: 'adjustment_day',
        label: 'Hyran justeras den',
        type: 'text',
        section: 'tidsbestamd-hyra',
        default: '1 januari',
        help: 'Vilket datum varje år hyran justeras, t.ex. "1 januari".',
    },
    {
        key: 'schedule',
        label: 'Hyrestrappa',
        type: 'rent_schedule',
        section: 'tidsbestamd-hyra',
        baseAmountKey: 'move_in_rent',
        help: 'En rad per justering: datum, höjning i procent och beloppsbestämd månadshyra. Sätt procent så fylls kronor i automatiskt — eller tvärtom. Båda går att justera; kronorbeloppet har företräde.',
    },
];
export const sampleValues = {
    landlord_name: 'Bostadsägarna Stockholm AB',
    landlord_org_number: '556789-1234',
    tenant_name: 'Anna Andersson',
    tenant_id_number: '19850412-1234',
    cotenant_name: '',
    cotenant_id_number: '',
    contract_number: 'T-1007',
    parent_contract_number: 'A-2042',
    property_designation: 'Sandbacken 12',
    property_municipality: 'Stockholm',
    property_address: 'Götgatan 15, 116 46 Stockholm',
    apartment_official_number: '1201',
    move_in_rent: 12000,
    period_start: '2027-01-01',
    period_end: '2030-12-31',
    adjustment_day: '1 januari',
    schedule: [
        { date: '2027-01-01', percent: 3, amount: 12360 },
        { date: '2028-01-01', percent: 3, amount: 12731 },
        { date: '2029-01-01', percent: 2.5, amount: 13049 },
        { date: '2030-01-01', percent: 2.5, amount: 13375 },
    ],
};
const asSchedule = (val) => Array.isArray(val)
    ? val.map((r) => {
        const row = (r ?? {});
        return {
            date: typeof row.date === 'string' ? row.date : '',
            percent: typeof row.percent === 'number' ? row.percent : null,
            amount: typeof row.amount === 'number' ? row.amount : null,
        };
    })
    : [];
/**
 * Resolve the beloppsbestämda månadshyran for each step. The stored
 * `amount` wins ("kronorbeloppet har företräde" per the legal text); a row
 * with no stored amount falls back to a cumulative computation from the
 * previous amount (or the move-in rent) × (1 + percent/100), rounded.
 */
function computeSchedule(base, rows) {
    let running = base;
    return rows.map((r) => {
        let amount = r.amount;
        if (amount == null && running != null && typeof r.percent === 'number') {
            amount = Math.round(running * (1 + r.percent / 100));
        }
        running = amount ?? running;
        return { ...r, amount };
    });
}
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
export function Body({ values, mode, signatories, }) {
    const base = typeof values.move_in_rent === 'number' ? values.move_in_rent : null;
    const rows = asSchedule(values.schedule);
    const computed = computeSchedule(base, rows);
    const showCotenant = mode === 'blank' || !!values.cotenant_name;
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsx("p", { className: "mb-2 text-center text-[13px] font-semibold uppercase tracking-wide", children: "Till\u00E4ggsavtal om tidsbest\u00E4md hyra" }), (mode === 'blank' || !!values.contract_number) && (_jsxs("div", { className: "mb-4 text-center text-[10px] text-stone-700", children: [_jsx("span", { className: "font-sans uppercase tracking-[0.1em] text-stone-500", children: "Avtalsnummer:" }), ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.contract_number) })] })), _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsx(Section, { sectionRef: "hyresvard", title: "Hyresv\u00E4rd", children: _jsx(PartyBlock, { metadata: metadata, slot: "lessor", values: values }) }), _jsxs(Section, { sectionRef: "hyresgast", title: "Hyresg\u00E4st", children: [_jsx(PartyBlock, { metadata: metadata, slot: "lessee_primary", values: values }), showCotenant && (_jsx(PartyBlock, { metadata: metadata, slot: "lessee_co", values: values, subheading: "Medhyresg\u00E4st" }))] })] }), _jsxs(Section, { sectionRef: "hyresavtalet", title: "Hyresavtalet", children: [_jsx(FieldRow, { label: "Hyreskontrakt nr", children: v(values.parent_contract_number) }), _jsx(FieldRow, { label: "Fastighetsbeteckning", children: v(values.property_designation) }), _jsx(FieldRow, { label: "Kommun", children: v(values.property_municipality) }), _jsx(FieldRow, { label: "Postadress", children: v(values.property_address) }), _jsx(FieldRow, { label: "Officiellt l\u00E4genhetsnummer", children: v(values.apartment_official_number) })] }), _jsxs(Section, { sectionRef: "tidsbestamd-hyra", title: "Tidsbest\u00E4md hyra", children: [_jsxs("p", { children: ["Den avtalade hyran uppg\u00E5r vid inflyttningen till", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: base != null ? num(base) : PLACEHOLDER }), ' ', "kronor per m\u00E5nad."] }), _jsxs("p", { className: "mt-3", children: ["Parterna \u00E4r \u00F6verens om att hyran ska justeras den", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.adjustment_day) }), ' ', "varje \u00E5r under perioden fr\u00E5n och med", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.period_start) }), ' ', "till och med", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.period_end) }), ' ', "enligt nedanst\u00E5ende tabell."] }), _jsxs("table", { className: "mt-3 w-full border-collapse text-[10.5px]", children: [_jsx("thead", { children: _jsx("tr", { children: ['Datum då hyran gäller', 'Höjningsprocent', 'Beloppsbestämd månadshyra (kr)'].map((h) => (_jsx("th", { className: "border border-stone-400 bg-stone-100 px-2 py-1 text-left font-semibold", children: h }, h))) }) }), _jsx("tbody", { children: (computed.length > 0
                                    ? computed
                                    : [{ date: '', percent: null, amount: null }]).map((row, i) => (_jsxs("tr", { children: [_jsx("td", { className: "border border-stone-400 px-2 py-1 font-serif", children: row.date ? row.date : PLACEHOLDER }), _jsx("td", { className: "border border-stone-400 px-2 py-1 font-serif", children: typeof row.percent === 'number' ? `${num(row.percent)} %` : `${PLACEHOLDER} %` }), _jsx("td", { className: "border border-stone-400 px-2 py-1 font-serif", children: row.amount != null ? `${num(row.amount)} kr/månad` : `${PLACEHOLDER} kr/månad` })] }, i))) })] }), _jsx("p", { className: "mt-3", children: "Ber\u00E4kningen har gjorts med utg\u00E5ngspunkt i inflyttningshyran och angiven h\u00F6jningsprocent. Den hyra som g\u00E4ller f\u00F6r respektive period \u00E4r den beloppsbest\u00E4mda m\u00E5nadshyra i kronor som anges i tabellen." }), _jsx("p", { className: "mt-2", children: "Vid eventuell avvikelse mellan angiven h\u00F6jningsprocent och angiven m\u00E5nadshyra i kronor ska den beloppsbest\u00E4mda m\u00E5nadshyran i kronor ha f\u00F6retr\u00E4de." }), _jsx("p", { className: "mt-2", children: "Parterna \u00E4r \u00F6verens om att hyran under hyresf\u00F6rh\u00E5llandet ska utvecklas enligt de principer som framg\u00E5r av detta till\u00E4ggsavtal. Parternas gemensamma avsikt \u00E4r att \u00E4ven efter avtalsperiodens utg\u00E5ng tr\u00E4ffa en ny \u00F6verenskommelse om tidsbest\u00E4md hyra baserad p\u00E5 motsvarande modell och struktur, om inte v\u00E4sentligt f\u00F6r\u00E4ndrade marknadsf\u00F6rh\u00E5llanden, lagstiftning eller andra s\u00E4rskilda omst\u00E4ndigheter motiverar annat." })] }), _jsx(KeepTogether, { children: _jsx(Section, { sectionRef: "underskrifter", title: "Underskrifter", children: _jsx(SigningBlock, { parties: showCotenant
                            ? [
                                { label: 'Hyresvärd', role: 'lessor', ordinal: 0 },
                                { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
                                { label: 'Medhyresgäst', role: 'lessee', ordinal: 1 },
                            ]
                            : [
                                { label: 'Hyresvärd', role: 'lessor', ordinal: 0 },
                                { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
                            ], signatories: signatories }) }) })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map